import DocsLayout, { Callout } from '../../components/docs/DocsLayout';

export default function DocsGovernance() {
  return (
    <DocsLayout 
      title="Governance" 
      description="DAO structure and voting mechanisms"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          AIBL is governed by its token holders through a decentralized autonomous organization (DAO). This page explains how governance works and how you can participate.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Governance Structure</h2>
        
        <p className="text-white/70 mb-4">
          The AIBL DAO operates on a tiered proposal system:
        </p>

        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-semibold">AIP</span>
              <h3 className="font-semibold text-white">AIBL Improvement Proposals</h3>
            </div>
            <p className="text-sm text-white/60">
              Major protocol changes, tokenomics updates, and strategic decisions. Requires formal discussion period and supermajority vote.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-semibold">AGP</span>
              <h3 className="font-semibold text-white">AIBL Governance Proposals</h3>
            </div>
            <p className="text-sm text-white/60">
              Fee adjustments, parameter changes, and operational decisions. Standard voting period with simple majority.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs font-semibold">ARP</span>
              <h3 className="font-semibold text-white">AIBL Request Proposals</h3>
            </div>
            <p className="text-sm text-white/60">
              Community suggestions and feature requests. Temperature check before formal proposal.
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Voting Power</h2>
        
        <p className="text-white/70 mb-4">
          Voting power is determined by staked AIBL tokens:
        </p>

        <ul className="space-y-2 text-white/70">
          <li>• 1 staked AIBL = 1 vote</li>
          <li>• Delegation is supported (vote with tokens you don't own)</li>
          <li>• Quadratic voting for certain proposal types</li>
          <li>• Time-weighted voting bonus for long-term stakers</li>
        </ul>

        <Callout type="info" title="Vote Delegation">
          If you can't actively participate in governance, you can delegate your voting power to a trusted community member who will vote on your behalf.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Proposal Lifecycle</h2>

        <ol className="space-y-4 text-white/70">
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">1</span>
            <div>
              <strong className="text-white">Discussion (3-7 days)</strong>
              <p>Proposal is shared in the forum for community feedback and refinement.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">2</span>
            <div>
              <strong className="text-white">Temperature Check (2 days)</strong>
              <p>Snapshot poll to gauge community interest before formal vote.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">3</span>
            <div>
              <strong className="text-white">Formal Vote (5 days)</strong>
              <p>On-chain voting period. Quorum must be met for the vote to be valid.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">4</span>
            <div>
              <strong className="text-white">Timelock (2 days)</strong>
              <p>Passed proposals enter a timelock before execution for security review.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">5</span>
            <div>
              <strong className="text-white">Execution</strong>
              <p>Changes are implemented by the protocol or multisig.</p>
            </div>
          </li>
        </ol>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Governance Parameters</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/50 font-medium">Parameter</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">AIP</th>
                <th className="text-left py-3 px-4 text-white/50 font-medium">AGP</th>
              </tr>
            </thead>
            <tbody className="text-white/70">
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Proposal Threshold</td>
                <td className="py-3 px-4">100,000 AIBL</td>
                <td className="py-3 px-4">10,000 AIBL</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Quorum</td>
                <td className="py-3 px-4">10% of supply</td>
                <td className="py-3 px-4">4% of supply</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-4">Pass Threshold</td>
                <td className="py-3 px-4">66% supermajority</td>
                <td className="py-3 px-4">50% + 1</td>
              </tr>
              <tr>
                <td className="py-3 px-4">Voting Period</td>
                <td className="py-3 px-4">7 days</td>
                <td className="py-3 px-4">5 days</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Getting Involved</h2>
        
        <ul className="space-y-2 text-white/70">
          <li>• Join the <a href="#" className="text-blue-400 hover:underline">AIBL Forum</a> to participate in discussions</li>
          <li>• Follow <a href="https://x.com/aibl" className="text-blue-400 hover:underline">@aibl on X</a> for governance updates</li>
          <li>• Stake AIBL tokens to gain voting power</li>
          <li>• Delegate your votes if you prefer passive participation</li>
        </ul>
      </section>
    </DocsLayout>
  );
}
